// Cloud repository — Firestore (records) + GCS (blobs) via the backend function.

import type { AtomStatus, NewsAtom, Source, StoryEvent, Topic } from './types';
import type {
  Capabilities,
  EnrichEventInput,
  EventBankResult,
  EventEnrichment,
  IngestUrlResult,
  JobHandle,
  JobState,
  MediaUrlResult,
  ProduceOptions,
  ProductionRecord,
  RebuildBankResult,
  Recipe,
  RecipeInput,
  Repo,
  TopicBundle,
} from './repo';
import { apiFetch } from './api';

export const cloudRepo: Repo = {
  async listTopics() {
    const { topics } = await apiFetch<{ topics: Topic[] }>('/topics');
    return topics;
  },
  createTopic(input) {
    return apiFetch<Topic>('/topics', { method: 'POST', body: JSON.stringify(input) });
  },
  renameTopic(topicId, title) {
    return apiFetch<{ title: string }>(`/topics/${topicId}`, { method: 'PATCH', body: JSON.stringify({ title }) });
  },
  suggestTitle(topicId) {
    return apiFetch<{ title: string }>(`/topics/${topicId}/suggest-title`, { method: 'POST' });
  },
  getBundle(topicId) {
    return apiFetch<TopicBundle>(`/topics/${topicId}`);
  },
  async saveSources(topicId, sources: Source[], csvRaw) {
    await apiFetch(`/topics/${topicId}/sources`, {
      method: 'PUT',
      body: JSON.stringify({ sources, csvRaw }),
    });
  },
  async deleteSource(topicId, sourceId) {
    await apiFetch(`/topics/${topicId}/sources/${sourceId}`, { method: 'DELETE' });
  },
  async saveAtoms(topicId, atoms: NewsAtom[]) {
    await apiFetch(`/topics/${topicId}/atoms`, {
      method: 'PUT',
      body: JSON.stringify({ atoms }),
    });
  },
  async setAtomStatus(atomId, status: AtomStatus) {
    await apiFetch(`/atoms/${atomId}`, { method: 'PATCH', body: JSON.stringify({ status }) });
  },
  async deleteTopic(topicId) {
    await apiFetch(`/topics/${topicId}`, { method: 'DELETE' });
  },
  startAtomize(topicId) {
    return apiFetch<JobHandle>(`/topics/${topicId}/atomize`, { method: 'POST' });
  },
  publishToIconik(topicId) {
    return apiFetch<JobHandle>(`/topics/${topicId}/publish-iconik`, { method: 'POST' });
  },
  produce(topicId, opts?: ProduceOptions) {
    return apiFetch<JobHandle>(`/topics/${topicId}/produce`, {
      method: 'POST',
      body: JSON.stringify(opts ?? {}),
    });
  },
  produceEvent(topicId, eventId, opts?: ProduceOptions) {
    return apiFetch<JobHandle>(`/topics/${topicId}/events/${eventId}/produce`, {
      method: 'POST',
      body: JSON.stringify(opts ?? {}),
    });
  },
  getJob(jobId) {
    return apiFetch<JobState>(`/jobs/${jobId}`);
  },
  getCapabilities() {
    return apiFetch<Capabilities>('/capabilities');
  },
  listProductions(topicId) {
    return apiFetch<{ productions: ProductionRecord[] }>(`/topics/${topicId}/produce`);
  },
  ingestUrl(topicId, filename, contentType) {
    return apiFetch<IngestUrlResult>(`/topics/${topicId}/ingest-url`, {
      method: 'POST',
      body: JSON.stringify({ filename, contentType }),
    });
  },
  startAnalyze(topicId, sourceId, contentType) {
    return apiFetch<JobHandle>(`/topics/${topicId}/sources/${sourceId}/analyze`, {
      method: 'POST',
      body: JSON.stringify({ contentType }),
    });
  },
  mediaUrl(topicId, sourceId) {
    // Direct GCS v4 signed READ URL — natively supports Range/206 so the browser
    // can seek to an atom's in-point. Bypasses the Hosting→function proxy (which
    // strips Range, buffers, and caps the payload), so large videos actually play.
    return apiFetch<MediaUrlResult>(`/topics/${topicId}/sources/${sourceId}/media-url`);
  },
  clusterEvents(topicId) {
    return apiFetch<JobHandle>(`/topics/${topicId}/cluster-events`, { method: 'POST' });
  },
  saveEvents(topicId, events: StoryEvent[]) {
    return apiFetch<{ events: StoryEvent[] }>(`/topics/${topicId}/events`, {
      method: 'PUT',
      body: JSON.stringify({ events }),
    });
  },
  enrichEvent(topicId, input: EnrichEventInput) {
    return apiFetch<EventEnrichment>(`/topics/${topicId}/enrich-event`, {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },
  getEventBank() {
    return apiFetch<EventBankResult>('/event-bank');
  },
  rebuildEventBank() {
    return apiFetch<RebuildBankResult>('/event-bank', { method: 'POST' });
  },
  listRecipes(includeArchived?: boolean) {
    return apiFetch<{ recipes: Recipe[] }>(`/recipes${includeArchived ? '?includeArchived=1' : ''}`);
  },
  createRecipe(input: RecipeInput) {
    return apiFetch<Recipe>('/recipes', { method: 'POST', body: JSON.stringify(input) });
  },
  updateRecipe(recipeId, input) {
    return apiFetch<Recipe>(`/recipes/${recipeId}`, { method: 'PATCH', body: JSON.stringify(input) });
  },
  archiveRecipe(recipeId) {
    return apiFetch<Recipe>(`/recipes/${recipeId}`, { method: 'DELETE' });
  },
};
